import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Warehouse, Plus, PackageCheck } from "lucide-react";
import { HubAccessRequests } from "@/components/AdminGovernance";
import { HubAddress } from "@/components/HubAddress";

const EMPTY_HUB = { name: "", country: "Royaume-Uni", city: "", address: "", postal_code: "", phone: "", hours: "", active: true, marketplace_active: true, shopship_active: false, currency: "GBP", air: true, sea: true };
const HUB_COUNTRIES = ["Royaume-Uni", "France", "Belgique", "Allemagne", "Espagne", "Italie", "Pays-Bas", "Portugal", "États-Unis", "Canada", "Chine", "Turquie", "Émirats arabes unis"];

const STATUS_LABEL = {
  created: ["bg-secondary text-primary", "Attente réception"],
  at_hub: ["bg-blue-100 text-blue-700", "Au Hub"],
  awaiting_partner: ["bg-amber-100 text-amber-700", "Choix groupageur"],
  partner_selected: ["bg-purple-100 text-purple-700", "Attente collecte"],
  collected: ["bg-emerald-100 text-emerald-700", "Collecté"],
  paid_pending: ["bg-emerald-100 text-emerald-700", "Prix validé"],
  shipped: ["bg-primary text-white", "Expédié"],
  arrived: ["bg-primary text-white", "Arrivé"],
  delivered: ["bg-emerald-600 text-white", "Livré"],
};

const DECISION_LABEL = { wait: "Attendre", consolidate: "Consolider", ship_now: "Tel quel" };

export const AdminHubs = () => {
  const [hubs, setHubs] = useState([]);
  const [ships, setShips] = useState([]);
  const [form, setForm] = useState(EMPTY_HUB);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [recv, setRecv] = useState({ reference: "", supplier_name: "", cartons: 1, weight_kg: 0, length_cm: 0, width_cm: 0, height_cm: 0, condition: "good", notes: "" });

  const load = () => {
    api.get("/admin/hubs").then(({ data }) => setHubs(data));
    api.get("/admin/hubs/shipments").then(({ data }) => setShips(data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.city || !form.address) { toast.error("Nom, ville et adresse requis"); return; }
    try {
      if (editId) await api.put(`/admin/hubs/${editId}`, form);
      else await api.post("/admin/hubs", form);
      toast.success(editId ? "Hub mis à jour" : "Hub créé");
      setOpen(false); setForm(EMPTY_HUB); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggle = async (h, field, val) => { await api.put(`/admin/hubs/${h.id}`, { [field]: val }); load(); };

  const receive = async () => {
    if (!recv.reference || !recv.supplier_name) { toast.error("Référence et fournisseur requis"); return; }
    try {
      await api.post("/admin/hubs/receive", {
        ...recv, cartons: Number(recv.cartons), weight_kg: Number(recv.weight_kg),
        length_cm: Number(recv.length_cm), width_cm: Number(recv.width_cm), height_cm: Number(recv.height_cm), photos: [],
      });
      toast.success("Colis enregistré au Hub — le client est notifié");
      setRecv({ reference: "", supplier_name: "", cartons: 1, weight_kg: 0, length_cm: 0, width_cm: 0, height_cm: 0, condition: "good", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-8">
      {/* Liste des hubs */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Warehouse className="h-5 w-5 text-accent" /> Hubs Colis2Go</h2>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY_HUB); setEditId(null); } }}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-hub-btn" className="rounded-sm bg-accent text-white gap-1"><Plus className="h-4 w-4" /> Ajouter un Hub</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Modifier le Hub" : "Nouveau Hub Colis2Go"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nom</Label><Input data-testid="hub-name" className="rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hub Colis2Go Manchester" /></div>
                <div><Label>Pays</Label>
                  <Input data-testid="hub-country" className="rounded-sm" list="hub-countries-list" placeholder="Rechercher ou saisir…"
                    value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                  <datalist id="hub-countries-list">{HUB_COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist>
                </div>
                <div><Label>Ville</Label>
                  <Input data-testid="hub-city" className="rounded-sm" list="hub-cities-list" placeholder="Rechercher ou saisir…"
                    value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  <datalist id="hub-cities-list">{[...new Set(hubs.map((x) => x.city).filter(Boolean))].map((c) => <option key={c} value={c} />)}</datalist>
                </div>
                <div className="col-span-2"><Label>Adresse complète (saisie libre)</Label><Input data-testid="hub-address" className="rounded-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>Code postal</Label><Input data-testid="hub-postal" className="rounded-sm" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="L21 7NQ" /></div>
                <div><Label>Téléphone</Label><Input className="rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Horaires</Label><Input className="rounded-sm" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="Lun-Sam 09:00-18:00" /></div>
                <div><Label>Devise</Label>
                  <select data-testid="hub-currency" className="w-full h-9 border border-border bg-card px-2 text-sm" value={form.currency || "EUR"} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                    <option value="GBP">GBP (£)</option><option value="EUR">EUR (€)</option><option value="USD">USD ($)</option>
                  </select>
                </div>
                <div className="flex items-end gap-4 pb-1 text-sm">
                  <label className="flex items-center gap-1.5"><Switch data-testid="hub-air" checked={!!form.air} onCheckedChange={(v) => setForm({ ...form, air: v })} /> Aérien</label>
                  <label className="flex items-center gap-1.5"><Switch data-testid="hub-sea" checked={!!form.sea} onCheckedChange={(v) => setForm({ ...form, sea: v })} /> Maritime</label>
                </div>
                <label className="col-span-2 flex items-center gap-2 text-sm">
                  <Switch data-testid="hub-visible" checked={!!form.marketplace_active} onCheckedChange={(v) => setForm({ ...form, marketplace_active: v })} /> Visible côté client
                </label>
              </div>
              <DialogFooter><Button data-testid="save-hub-btn" className="rounded-sm bg-accent text-white" onClick={save}>Enregistrer</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {hubs.map((h) => (
            <div key={h.id} data-testid={`hub-card-${h.id}`} className="border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <div className="font-heading font-bold">{h.name}</div>
                <Button size="sm" variant="outline" className="rounded-sm" data-testid={`edit-hub-${h.id}`}
                  onClick={() => { setForm({ ...EMPTY_HUB, ...h }); setEditId(h.id); setOpen(true); }}>Modifier</Button>
              </div>
              <div className="mt-1"><HubAddress hub={h} /></div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm">
                <span>{h.shipments_count} expédition(s)</span>
                <span className="text-accent font-semibold">{h.at_hub_count} en cours au Hub</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-5 text-sm">
                <label className="flex items-center gap-2"><Switch data-testid={`hub-active-${h.id}`} checked={h.active} onCheckedChange={(v) => toggle(h, "active", v)} /> Actif</label>
                <label className="flex items-center gap-2"><Switch data-testid={`hub-visible-${h.id}`} checked={h.marketplace_active} onCheckedChange={(v) => toggle(h, "marketplace_active", v)} /> Visible côté client</label>
                <label className="flex items-center gap-2"><Switch data-testid={`hub-online-${h.id}`} checked={h.shopship_active} onCheckedChange={(v) => toggle(h, "shopship_active", v)} /> Achats en ligne</label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Demandes d'accès aux Hubs (gouvernance) */}
      <HubAccessRequests />

      {/* Réception au Hub */}
      <div className="border border-border bg-card p-5">
        <h3 className="font-heading font-bold flex items-center gap-2"><PackageCheck className="h-5 w-5 text-accent" /> Réception au Hub</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <div><Label>Référence (C2G-…)</Label><Input data-testid="hubrecv-ref" className="rounded-sm" value={recv.reference} onChange={(e) => setRecv({ ...recv, reference: e.target.value })} /></div>
          <div><Label>Fournisseur / Expéditeur</Label><Input data-testid="hubrecv-supplier" className="rounded-sm" value={recv.supplier_name} onChange={(e) => setRecv({ ...recv, supplier_name: e.target.value })} /></div>
          <div><Label>Cartons</Label><Input data-testid="hubrecv-cartons" type="number" className="rounded-sm" value={recv.cartons} onChange={(e) => setRecv({ ...recv, cartons: e.target.value })} /></div>
          <div><Label>Poids (kg)</Label><Input data-testid="hubrecv-weight" type="number" className="rounded-sm" value={recv.weight_kg} onChange={(e) => setRecv({ ...recv, weight_kg: e.target.value })} /></div>
          <div><Label>Dimensions L×l×H (cm)</Label>
            <div className="flex gap-1">
              <Input type="number" className="rounded-sm" placeholder="L" value={recv.length_cm} onChange={(e) => setRecv({ ...recv, length_cm: e.target.value })} />
              <Input type="number" className="rounded-sm" placeholder="l" value={recv.width_cm} onChange={(e) => setRecv({ ...recv, width_cm: e.target.value })} />
              <Input type="number" className="rounded-sm" placeholder="H" value={recv.height_cm} onChange={(e) => setRecv({ ...recv, height_cm: e.target.value })} />
            </div>
          </div>
          <div><Label>État</Label>
            <Select value={recv.condition} onValueChange={(v) => setRecv({ ...recv, condition: v })}>
              <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="good">Bon état</SelectItem><SelectItem value="damaged">Endommagé</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <Button data-testid="hubrecv-submit" className="mt-4 rounded-sm bg-accent text-white" onClick={receive}>Enregistrer la réception</Button>
      </div>

      {/* Expéditions Hub */}
      <div className="overflow-x-auto">
        <h3 className="font-heading font-bold mb-3">Expéditions via Hub ({ships.length})</h3>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Référence</TableHead><TableHead>Client</TableHead><TableHead>Hub</TableHead><TableHead>Destination</TableHead>
            <TableHead>Colis</TableHead><TableHead>Décision</TableHead><TableHead>Groupageur</TableHead><TableHead>Statut</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {ships.map((s) => {
              const [cls, label] = STATUS_LABEL[s.status] || ["bg-secondary text-primary", s.status];
              return (
                <TableRow key={s.id} data-testid={`hubship-${s.reference}`}>
                  <TableCell className="font-semibold">{s.reference}</TableCell>
                  <TableCell>{s.customer_name}</TableCell>
                  <TableCell className="text-xs">{s.hub_name}</TableCell>
                  <TableCell className="text-xs">{s.dest_city}, {s.dest_country}</TableCell>
                  <TableCell className="text-xs">{(s.packages || []).length} · {s.total_weight} kg</TableCell>
                  <TableCell className="text-xs">{DECISION_LABEL[s.hub_decision] || "—"}</TableCell>
                  <TableCell className="text-xs">{s.partner_name || "—"}</TableCell>
                  <TableCell><Badge className={`rounded-sm ${cls}`}>{label}</Badge></TableCell>
                </TableRow>
              );
            })}
            {ships.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Aucune expédition via Hub pour le moment.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
